import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react'

/**
 * Tek tarih seçici — solda hızlı seçenekler (Bugün, Yarın, Gelecek hafta…), sağda ay takvimi.
 *
 * Değer ISO gün dizesidir ('YYYY-MM-DD', yerel saat — toISOString KULLANILMAZ, UTC kayması).
 * `min` / `max` dışındaki gün ve hızlı seçenek pasif görünür, seçilemez.
 * Açılır pencere `createPortal` ile body'ye çizilir: Modal ve yan paneller `overflow` taşıdığı
 * için içeride absolute/fixed pencere kırpılır (.claude/rules/chat.md aynı tuzak).
 *
 * presets: 'future' (vade, ihtiyaç, kapanış) · 'past' (ödeme, iade tarihi) · ya da
 *          [{ label, date: Date }] dizisi.
 */

const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık']
const DAYS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz']

const pad = (n) => String(n).padStart(2, '0')
export const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
const parseIso = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''))
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Number.isNaN(d.getTime()) ? null : d
}
const show = (d) => (d ? `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}` : '')
const shortLabel = (d) => `${d.getDate()} ${MONTHS[d.getMonth()].slice(0, 3)}`
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d }
const plus = (n) => { const d = startOfToday(); d.setDate(d.getDate() + n); return d }

function presetList(kind) {
  const t = startOfToday()
  if (kind === 'past') {
    const monthStart = new Date(t.getFullYear(), t.getMonth(), 1)
    const lastFri = plus(-(((t.getDay() - 5) + 7) % 7 || 7))
    return [
      { label: 'Bugün', date: t }, { label: 'Dün', date: plus(-1) },
      { label: 'Geçen Cuma', date: lastFri }, { label: 'Geçen hafta', date: plus(-7) },
      { label: 'Ay başı', date: monthStart },
    ]
  }
  const toFri = ((5 - t.getDay()) + 7) % 7
  const nextMon = plus(((1 - t.getDay()) + 7) % 7 || 7)
  return [
    { label: 'Bugün', date: t }, { label: 'Yarın', date: plus(1) },
    { label: toFri === 0 ? 'Gelecek Cuma' : 'Bu Cuma', date: plus(toFri || 7) },
    { label: 'Gelecek Pazartesi', date: nextMon },
    { label: '2 hafta sonra', date: plus(14) },
    { label: '1 ay sonra', date: plus(30) },
    { label: 'Ay sonu', date: new Date(t.getFullYear(), t.getMonth() + 1, 0) },
  ]
}

function monthGrid(year, month) {
  const first = new Date(year, month, 1)
  const lead = (first.getDay() + 6) % 7          // Pazartesi başlangıç
  const start = new Date(year, month, 1 - lead)
  return Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i))
}

export default function DatePicker({
  value, onChange, min, max, presets = 'future', placeholder = 'Tarih seçin',
  clearable = false, disabled = false, className = '', ariaLabel,
}) {
  const [open, setOpen] = useState(false)
  const sel = parseIso(value)
  const minD = parseIso(min)
  const maxD = parseIso(max)
  const [cursor, setCursor] = useState(() => sel || startOfToday())
  const [pos, setPos] = useState(null)
  const btnRef = useRef(null)
  const popRef = useRef(null)

  const quick = useMemo(() => (Array.isArray(presets) ? presets : presetList(presets)), [presets, open])
  const outOfRange = useCallback((d) => (minD && d < minD) || (maxD && d > maxD), [minD, maxD])

  const place = useCallback(() => {
    const b = btnRef.current?.getBoundingClientRect()
    if (!b) return
    const w = Math.min(window.innerWidth - 16, 440)
    const h = popRef.current?.offsetHeight || 330
    let left = Math.min(b.left, window.innerWidth - w - 8)
    left = Math.max(8, left)
    const below = window.innerHeight - b.bottom
    const top = below < h + 8 && b.top > h + 8 ? b.top - h - 4 : b.bottom + 4
    setPos({ left, top, width: w })
  }, [])

  useLayoutEffect(() => { if (open) place() }, [open, place, cursor])
  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => {
      if (popRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return
      setOpen(false)
    }
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false) } }
    const onMove = () => place()
    document.addEventListener('mousedown', onDown, true)
    document.addEventListener('keydown', onKey, true)
    window.addEventListener('resize', onMove)
    window.addEventListener('scroll', onMove, true)
    return () => {
      document.removeEventListener('mousedown', onDown, true)
      document.removeEventListener('keydown', onKey, true)
      window.removeEventListener('resize', onMove)
      window.removeEventListener('scroll', onMove, true)
    }
  }, [open, place])

  const toggle = () => {
    if (disabled) return
    if (!open) setCursor(sel || startOfToday())
    setOpen((v) => !v)
  }
  const pick = (d) => {
    if (outOfRange(d)) return
    onChange?.(isoDay(d))
    setOpen(false)
  }
  const today = startOfToday()
  const same = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  const grid = monthGrid(cursor.getFullYear(), cursor.getMonth())

  return (
    <>
      <div className={`relative ${className}`}>
        <button ref={btnRef} type="button" onClick={toggle} disabled={disabled} aria-label={ariaLabel || placeholder}
          aria-haspopup="dialog" aria-expanded={open}
          className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left border rounded-lg bg-white transition-colors
            focus:outline-none focus:ring-2 focus:ring-primary-500/30
            ${open ? 'border-primary-400' : 'border-gray-200 hover:border-gray-300'}
            ${disabled ? 'opacity-60 cursor-not-allowed bg-gray-50' : ''}`}>
          <Calendar size={15} className={sel ? 'text-primary-500' : 'text-gray-400'} />
          <span className={`flex-1 tabular-nums ${sel ? 'text-gray-900' : 'text-gray-400'}`}>{sel ? show(sel) : placeholder}</span>
        </button>
        {clearable && sel && !disabled && (
          <button type="button" onClick={() => onChange?.('')} aria-label="Tarihi temizle"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100">
            <X size={14} />
          </button>
        )}
      </div>

      {open && createPortal(
        <div ref={popRef} role="dialog" aria-label="Tarih seç"
          style={pos ? { left: pos.left, top: pos.top, width: pos.width } : { visibility: 'hidden' }}
          onMouseDown={(e) => e.stopPropagation()}
          className="fixed z-[130] bg-white border border-gray-200 rounded-xl shadow-2xl flex flex-col sm:flex-row overflow-hidden">
          {/* Hızlı seçenekler — dar ekranda üstte yatay çipler */}
          <div className="sm:w-[150px] shrink-0 border-b sm:border-b-0 sm:border-r border-gray-100 bg-gray-50/70 p-1.5
                          flex sm:flex-col gap-1 overflow-x-auto">
            {quick.map((q) => {
              const off = outOfRange(q.date)
              const on = same(q.date, sel)
              return (
                <button key={q.label} type="button" disabled={off} onClick={() => pick(q.date)}
                  className={`shrink-0 flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg text-left text-[12.5px] transition-colors
                    ${on ? 'bg-primary-500 text-white' : 'text-gray-700 hover:bg-white hover:shadow-sm'}
                    ${off ? 'opacity-35 cursor-not-allowed hover:bg-transparent hover:shadow-none' : ''}`}>
                  <span className="font-medium whitespace-nowrap">{q.label}</span>
                  <span className={`text-[11px] tabular-nums whitespace-nowrap ${on ? 'text-white/80' : 'text-gray-400'}`}>{shortLabel(q.date)}</span>
                </button>
              )
            })}
          </div>

          {/* Ay takvimi */}
          <div className="flex-1 p-3">
            <div className="flex items-center gap-1 mb-2">
              <button type="button" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
                className="p-1 rounded-lg hover:bg-gray-100" aria-label="Önceki ay"><ChevronLeft size={16} className="text-gray-500" /></button>
              <span className="flex-1 text-center text-sm font-semibold text-gray-800">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</span>
              <button type="button" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
                className="p-1 rounded-lg hover:bg-gray-100" aria-label="Sonraki ay"><ChevronRight size={16} className="text-gray-500" /></button>
            </div>
            <div className="grid grid-cols-7">
              {DAYS.map((d) => <div key={d} className="h-7 grid place-items-center text-[11px] font-semibold text-gray-400">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-0.5">
              {grid.map((d, i) => {
                const outside = d.getMonth() !== cursor.getMonth()
                const off = outOfRange(d)
                const isSel = same(d, sel)
                const isToday = same(d, today)
                return (
                  <button key={i} type="button" disabled={off} onClick={() => pick(d)}
                    className={`h-8 rounded-md text-xs tabular-nums transition-colors
                      ${isSel ? 'bg-primary-500 text-white font-bold'
                        : off ? 'text-gray-200 cursor-not-allowed'
                          : outside ? 'text-gray-300 hover:bg-gray-100'
                            : 'text-gray-700 hover:bg-gray-100'}
                      ${isToday && !isSel ? 'ring-2 ring-inset ring-primary-300 font-semibold' : ''}`}>
                    {d.getDate()}
                  </button>
                )
              })}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}
